<template>
  <div class="bg-white rounded-lg border border-gray-200 h-full flex flex-col min-h-0">
    <!-- Header: Only title (sticky) -->
    

    <!-- Messages (scrollable) -->
    <div class="divide-y divide-gray-200 flex-1 overflow-y-auto min-h-0">
      <div v-for="(m, i) in messages" :key="i" class="px-4 py-5 sm:px-6 last:border-b border-gray-200">
        <div class="flex items-baseline justify-between">
          <div class="text-sm">
            <span :class="['inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium', store.getNoteTypeBadgeClasses(m.noteType)]">{{ noteTypeName(m.noteType) }}</span>
            <span v-if="m.email" class="ml-2 text-gray-500">{{ m.email }}</span>
          </div>
          <div class="text-xs text-gray-500">{{ m.time }}</div>
        </div>
        <div class="mt-3 text-sm text-gray-700 whitespace-pre-line">
          <template v-for="(seg, idx) in splitContentSegments(m.body)" :key="idx">
            <span v-if="seg.type === 'text'">{{ seg.text }}</span>
            <span v-else :class="tagBadgeClasses">#{{ seg.text }}</span>
          </template>
        </div>
        <div v-if="m.list && m.list.length" class="mt-3">
          <ul class="list-disc pl-5 text-sm text-gray-700">
            <li v-for="(li, j) in m.list" :key="j">{{ li }}</li>
          </ul>
        </div>
        <div v-if="m.attachments && m.attachments.length" class="mt-3 flex flex-wrap gap-2">
          <a v-for="(a, k) in m.attachments" :key="k" href="#" @click.prevent="handleDownload(a)" class="inline-flex items-center gap-1 rounded-md bg-gray-50 px-2 py-1 text-xs text-indigo-600 ring-1 ring-inset ring-gray-300 hover:underline">
            <PaperClipIcon class="h-4 w-4 text-gray-500" aria-hidden="true" />
            <span>{{ a.name }}</span>
          </a>
        </div>
      </div>
    </div>

    <!-- Reply area (UI only, pinned bottom) -->
    <div class="border-t border-gray-200 px-4 py-4 sm:px-6 shrink-0 bg-white">
      <label class="block text-sm font-medium text-gray-900 mb-2">Your reply</label>
      <div class="relative">
        <textarea
          ref="draftRef"
          v-model="draft"
          rows="4"
          class="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
          placeholder="Type your reply here... Use # to insert tags"
          @input="handleDraftInput"
          @keydown.down.prevent="moveSuggest(1)"
          @keydown.up.prevent="moveSuggest(-1)"
          @keydown.enter.prevent="applyCurrentSuggest"
        ></textarea>
        <Float :flip="0" transform portal placement="bottom-start" :offset="6" v-if="showSuggest && suggestions.length">
          <span ref="caretAnchor" :style="caretAnchorStyle" class="absolute h-0 w-0"></span>
          <div class="z-10 w-64 max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow">
            <div v-for="(s, idx) in suggestions" :key="s" @mousedown.prevent="applySuggest(s)" :class="['px-3 py-1.5 text-sm cursor-pointer', idx===suggestIndex ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100']">#{{ s }}</div>
          </div>
        </Float>
      </div>
      <div class="mt-3 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <input ref="fileInput" type="file" multiple class="sr-only" @change="handleFiles" />
          <button type="button" class="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900" @click="openFilePicker">
            <PaperClipIcon class="h-4 w-4" aria-hidden="true" />
            Attach
          </button>

          <!-- Note type selector (split button style) -->
          <Listbox as="div" v-model="selectedNoteType">
            <ListboxLabel class="sr-only">Change note type</ListboxLabel>
            <Float :flip="0" transform portal placement="bottom-start" :offset="6">
              <div class="inline-flex divide-x divide-indigo-700 rounded-md outline-hidden">
                <div class="inline-flex items-center gap-x-1.5 rounded-l-md bg-indigo-600 px-3 py-2 text-white">
                  <CheckIcon class="-ml-0.5 h-5 w-5" aria-hidden="true" />
                  <p class="text-sm font-semibold">{{ selectedNoteType.title }}</p>
                </div>
                <ListboxButton class="inline-flex items-center rounded-l-none rounded-r-md bg-indigo-600 p-2 hover:bg-indigo-700 focus-visible:outline-2 focus-visible:outline-indigo-400">
                  <span class="sr-only">Change note type</span>
                  <ChevronDownIcon class="h-5 w-5 text-white forced-colors:text-[Highlight]" aria-hidden="true" />
                </ListboxButton>
              </div>

              <transition leave-active-class="transition ease-in duration-100" leave-from-class="" leave-to-class="opacity-0">
                <ListboxOptions class="z-10 mt-2 w-72 origin-top-left divide-y divide-gray-200 overflow-hidden rounded-md bg-white shadow-lg outline-1 outline-black/5">
                  <ListboxOption as="template" v-for="opt in noteTypeOptions" :key="opt.title" :value="opt" v-slot="{ active, selected }">
                    <li :class="[active ? 'bg-indigo-600 text-white' : 'text-gray-900', 'cursor-default p-4 text-sm select-none']">
                      <div class="flex flex-col">
                        <div class="flex justify-between">
                          <p :class="selected ? 'font-semibold' : 'font-normal'">{{ opt.title }}</p>
                          <span v-if="selected" :class="active ? 'text-white' : 'text-indigo-600'">
                            <CheckIcon class="h-5 w-5" aria-hidden="true" />
                          </span>
                        </div>
                        <p :class="[active ? 'text-indigo-200' : 'text-gray-500', 'mt-2']">{{ opt.description }}</p>
                      </div>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </Float>
          </Listbox>
        </div>
        <button type="button" class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:opacity-50" :disabled="!canSend" @click="handleSend">Reply</button>
      </div>
      <div v-if="pendingBinaries.length" class="mt-2 flex flex-wrap gap-2">
        <span v-for="(a,i) in pendingBinaries" :key="i" class="inline-flex items-center gap-1 rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-700 ring-1 ring-inset ring-gray-300">
          {{ a.fileName }}
          <button type="button" class="text-gray-400 hover:text-gray-600" @click="removePending(i)">×</button>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick } from 'vue'
import { PaperClipIcon } from '@heroicons/vue/24/outline'
import { useMyStore } from '@/stores/mystore'
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { CheckIcon, ChevronDownIcon } from '@heroicons/vue/20/solid'
import { NoteType } from '@/api/ApiTypes'
import { Float } from '@headlessui-float/vue'
import ApiService from '@/api/ApiService'

const store = useMyStore()
const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api'
const apiService = new ApiService(apiBase, localStorage.getItem('auth_token_for_mani') || undefined)
function downloadUrl(id: number) {
  return `${apiBase}/notes/DownloadAttachment/${id}`
}

function noteTypeName(t: number): string {
  const map: Record<number, string> = {
    [NoteType.General]: 'General',
    [NoteType.Research]: 'Research',
    [NoteType.Idea]: 'Idea',
    [NoteType.Warning]: 'Warning',
    [NoteType.Journal]: 'Journal',
    [NoteType.Todo]: 'Todo',
    [NoteType.Compliance]: 'Compliance',
  }
  return map[t] || String(t)
}

type Segment = { type: 'text' | 'tag'; text: string }
function splitContentSegments(content: string): Segment[] {
  if (!content) return []
  const parts: Segment[] = []
  const regex = /#([\p{L}\p{N}_-]+)/gu
  let lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = regex.exec(content)) !== null) {
    const start = m.index
    const end = regex.lastIndex
    if (start > lastIndex) parts.push({ type: 'text', text: content.slice(lastIndex, start) })
    parts.push({ type: 'tag', text: m[1] })
    lastIndex = end
  }
  if (lastIndex < content.length) parts.push({ type: 'text', text: content.slice(lastIndex) })
  return parts
}

const tagBadgeClasses = 'inline-flex items-center rounded-md bg-indigo-50 px-1.5 py-0.5 text-indigo-700 mr-0'

function formatRelative(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const minutes = Math.floor(diffMs / 60000)
  if (minutes < 1) return 'az önce'
  if (minutes < 60) return `${minutes} dakika önce`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} saat önce`
  const days = Math.floor(hours / 24)
  return `${days} gün önce`
}

function formatTimestamp(input: any): string {
  try {
    const d = new Date(input)
    if (isNaN(d.getTime())) return String(input || '')
    const weekday = d.toLocaleDateString('tr-TR', { weekday: 'short' })
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    const rel = formatRelative(d)
    return `${weekday}, ${time} (${rel})`
  } catch {
    return String(input || '')
  }
}

const messages = computed(() => {
  // Adapt backend note shape to this UI's expected fields
  // Expecting fields like: author/name/email/content/createdDate/attachments
  // Fallbacks applied for robustness during UI-only phase
  return (store.notes || []).map((n: any) => ({
    author: n.authorName || 'User',
    email: n.authorEmail || null,
    time: n.createdDateText || formatTimestamp(n.createdDate || n.createdAt || n.created || ''),
    body: n.content || '',
    list: null,
    noteType: n.noteType || NoteType.General,
    attachments: (n.attachments || []).map((a: any) => ({ id: a.id, name: a.fileName || a.url || 'Attachment' }))
  }))
})

const draft = ref('')
const canSend = computed(() => draft.value.trim().length >= 2)

const noteTypeOptions = [
  { value: NoteType.General, title: 'General', description: 'Genel not' },
  { value: NoteType.Research, title: 'Research', description: 'Araştırma notu' },
  { value: NoteType.Idea, title: 'Idea', description: 'Fikir/hipotez' },
  { value: NoteType.Warning, title: 'Warning', description: 'Uyarı' },
  { value: NoteType.Journal, title: 'Journal', description: 'Günlük/yorum' },
  { value: NoteType.Todo, title: 'Todo', description: 'Yapılacak' },
  { value: NoteType.Compliance, title: 'Compliance', description: 'Uyum notu' },
]
const selectedNoteType = ref(noteTypeOptions[0])

const fileInput = ref<HTMLInputElement | null>(null)
type PendingBinary = { fileName: string; contentType: string | null; sizeBytes: number | null; binaryBase64: string | null }
const pendingBinaries = ref<PendingBinary[]>([])

// Tag suggest state (inline)
const draftRef = ref<HTMLTextAreaElement | null>(null)
const caretAnchor = ref<HTMLElement | null>(null)
const caretAnchorStyle = ref<Record<string,string>>({ left: '0px', top: '0px' })
const showSuggest = ref(false)
const suggestions = ref<string[]>([])
const suggestIndex = ref(0)
let lastHashStart = -1
let suggestTimer: number | null = null

function openFilePicker() {
  fileInput.value?.click()
}

function handleFiles(e: Event) {
  const input = e.target as HTMLInputElement
  const files = Array.from(input.files || [])
  for (const f of files) {
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result || '')
      pendingBinaries.value.push({ fileName: f.name, contentType: f.type || null, sizeBytes: f.size, binaryBase64: result })
    }
    reader.readAsDataURL(f)
  }
  input.value = ''
}

function removePending(idx: number) {
  pendingBinaries.value.splice(idx, 1)
}

function caretWordAfterHash(text: string, caret: number) {
  for (let i = caret - 1; i >= 0; i--) {
    const ch = text[i]
    if (ch === '#') {
      return { start: i, fragment: text.slice(i + 1, caret) }
    }
    if (ch === ' ' || ch === '\n' || ch === '\t') break
  }
  return null
}

function handleDraftInput() {
  const el = draftRef.value
  if (!el) return
  const caret = el.selectionStart || 0
  const match = caretWordAfterHash(draft.value, caret)
  if (match && match.fragment.length >= 1) {
    lastHashStart = match.start
    queueFetchSuggestions(match.fragment)
    updateCaretAnchorPosition()
  } else {
    showSuggest.value = false
  }
}

function queueFetchSuggestions(prefix: string) {
  if (suggestTimer) clearTimeout(suggestTimer)
  suggestTimer = window.setTimeout(async () => {
    await fetchSuggestions(prefix)
  }, 150)
}

async function fetchSuggestions(prefix: string) {
  try {
    const res = await apiService.post<string[]>('/notes/SuggestTags', { prefix: `#${prefix}`, limit: 10, includeDeleted: false } as any)
    if (res?.success && Array.isArray(res.data)) {
      suggestions.value = res.data
      showSuggest.value = suggestions.value.length > 0
      suggestIndex.value = 0
    } else {
      suggestions.value = []
      showSuggest.value = false
    }
  } catch {
    suggestions.value = []
    showSuggest.value = false
  }
}

function moveSuggest(delta: number) {
  if (!showSuggest.value || suggestions.value.length === 0) return
  suggestIndex.value = (suggestIndex.value + delta + suggestions.value.length) % suggestions.value.length
}

function applyCurrentSuggest() {
  if (!showSuggest.value) return
  applySuggest(suggestions.value[suggestIndex.value])
}

function applySuggest(tag: string) {
  const el = draftRef.value
  if (!el || lastHashStart < 0) return
  const caret = el.selectionStart || 0
  const before = draft.value.slice(0, lastHashStart)
  const after = draft.value.slice(caret)
  draft.value = `${before}#${tag}${after}`
  showSuggest.value = false
  suggestions.value = []
  suggestIndex.value = 0
  lastHashStart = -1
  // place caret after inserted tag
  nextTick(() => {
    const pos = (before + `#${tag}`).length
    el.setSelectionRange(pos, pos)
    el.focus()
  })
}

function updateCaretAnchorPosition() {
  const el = draftRef.value
  const anchor = caretAnchor.value
  if (!el || !anchor) return
  // Rough position near the caret by using selectionStart and line height estimates
  const style = window.getComputedStyle(el)
  const lineHeight = parseFloat(style.lineHeight || '20') || 20
  const paddingLeft = parseFloat(style.paddingLeft || '0') || 0
  const paddingTop = parseFloat(style.paddingTop || '0') || 0
  const cols = el.cols || 40
  const caret = el.selectionStart || 0
  const approxCol = caret % cols
  const approxRow = Math.floor(caret / cols)
  caretAnchorStyle.value = {
    left: `${paddingLeft + approxCol * 8}px`,
    top: `${paddingTop + (approxRow + 1) * lineHeight}px`,
  }
}

async function handleSend() {
  const text = draft.value.trim()
  if (!text) return
  await store.createNote(text, { noteType: selectedNoteType.value.value, binaries: pendingBinaries.value })
  draft.value = ''
  pendingBinaries.value = []
}

async function handleDownload(a: { id: number; name: string }) {
  try {
    const token = localStorage.getItem('auth_token_for_mani') || ''
    const resp = await fetch(downloadUrl(a.id), { headers: { Authorization: `Bearer ${token}` } })
    if (!resp.ok) throw new Error('Download failed')
    const blob = await resp.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = a.name || 'attachment'
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (e) {
    // optionally notify
  }
}
</script>

<style scoped>
</style>


